import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '~core/security/auth.service';
import { IconRegistryService } from '~core/utils/icon-registry.service';
import { AppStore } from 'src/app/app.store';
import { VERSION } from '~env/version';
import { environment } from '~env/environment';
import { Store, select } from '@ngrx/store';
import { getAuth } from '~ngrx/features/feature-auth/feature-auth.selectors';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ConfiguracoesType, emptyConfiguracoesType } from '~model/vo/grupo-usuario';
import { AppConfigService } from '~core/background/app-config.service';
import {
  entityConfig,
  entityConfigIntegracoes,
  entityConfigPrestacaoContas,
  entityConfigTrataviva,
} from '~ngrx/entitys/entity-configuracao/entity-configuracao.selectors';

@Component({
  selector: 'app-settings-menu',
  templateUrl: './settings-menu.component.html',
  styleUrls: ['./settings-menu.component.scss'],
})
export class SettingsMenuComponent implements OnInit {
  @Input() menuOpened!: BehaviorSubject<string>;
  @Input() sidenavExpanse!: BehaviorSubject<boolean>;
  @Input() roles!: BehaviorSubject<{ [k: string]: boolean }>;
  @Input() alertesPadding: number = 0;
  @Output() voltarParaMenuPrincipal = new EventEmitter<void>();

  // Paths para navegação
  PAINEL_PATH: string = '/configuracoes/painel';
  SISTEMA_PATH: string = '/configuracoes/sistema';
  INDICADORES_PATH: string = '/configuracoes/indicadores';
  TIPOS_INCIDENTE_PATH: string = '/configuracoes/incidentes';
  LAYOUTS_IMPORTACAO_PATH: string = '/configuracoes/importacao';
  RELATORIO_PATH: string = '/configuracoes/relatorio';
  TIPOS_DESPESAS_PATH: string = '/configuracoes/despesas';
  INTEGRACAO_PATH: string = '/configuracoes/integracao';
  TRATATIVA_PATH: string = '/configuracoes/tratativa';
  GRUPO_USUARIO_PATH: string = '/grupo-usuario';

  // Controle de permissões baseado no ConfiguracoesType
  paramsConfig: ConfiguracoesType = emptyConfiguracoesType();
  userRoles: string[] = [];

  // Observables para visibilidade condicional (como no ConfiguracoesComponent)
  $showIntegracao: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $showPrestacaoContas: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $showTratativas: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $utilizaGrupoUsuario: boolean = false;

  // Visibilidade de cada item do menu
  showPainel$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showSistema$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showIndicadores$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showIncidentes$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showImportacao$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showRelatorio$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showDespesas$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showIntegracaoMenu$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showTratativa$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showGrupoUsuario$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  hasLogoutRedirect: boolean = false;
  versaoState: Observable<string> = new BehaviorSubject(VERSION.version);
  descriptionState!: Observable<string>;
  description: string = environment.description;

  constructor(
    private router: Router,
    private authService: AuthService,
    private iconRegistryService: IconRegistryService,
    private store: AppStore,
    private translateService: TranslateService,
    private ngrxStore: Store,
    private jwt: JwtHelperService,
    private appConfig: AppConfigService
  ) {
  }

  ngOnInit(): void {
    this.iconRegistryService.setUp();
    this.hasLogoutRedirect = !!environment.logoutRedirectSession && !!sessionStorage.getItem(environment.logoutRedirectSession);
    this.descriptionState = this.translateService.get(this.description);

    // Carregar permissões do usuário do token JWT
    this.ngrxStore.select(getAuth).subscribe(auth => {
      if (auth.tokenRules) {
        const decoded = this.jwt.decodeToken(auth.tokenRules);

        // DEBUG DETALHADO
        console.log('=== DEBUG PERMISSÕES GRUPO USUÁRIO ===');
        console.log('Token decodificado COMPLETO:', decoded);
        console.log('decoded.grupo:', decoded.grupo);
        console.log('decoded.grupo?.configuracoes:', decoded.grupo?.configuracoes);
        console.log('auth.roles:', auth.roles);
        console.log('Tem grupo?', !!decoded.grupo);
        console.log('Tem configuracoes no grupo?', !!decoded.grupo?.configuracoes);
        console.log('======================================');

        if (decoded.grupo && decoded.grupo.configuracoes) {
          this.paramsConfig = decoded.grupo.configuracoes;
          console.log('USANDO permissões do GRUPO:', this.paramsConfig);
        } else {
          // Sem grupo: Admin recebe permissões completas; demais usuários recebem tudo negado
          const isAdminRole = auth.roles?.includes('Admin') ?? false;
          this.paramsConfig = emptyConfiguracoesType(isAdminRole);
          console.log('USANDO permissões PADRÃO (sem grupo):', this.paramsConfig);
        }
        // DEBUG: Visualizar permissões de ConfiguracoesType
        console.log('=== PERMISSÕES ConfiguracoesType ===');
        console.log('paramsConfig:', this.paramsConfig);
        console.log('Token decodificado:', decoded);
        console.log('=====================================');
      }
      if (auth.roles) {
        this.userRoles = auth.roles;
        console.log('userRoles:', this.userRoles);
      }
      this.updateMenuVisibility();
    });

    // Verificar configuração utilizaGrupoUsuario no sistema
    this.ngrxStore.pipe(select(entityConfig(this.appConfig.empresa)))
      .subscribe(config => {
        this.$utilizaGrupoUsuario = config.utilizaGrupoUsuario;
        this.updateMenuVisibility();
      });

    // Verificar configurações do sistema para Integração
    this.ngrxStore.pipe(select(entityConfigIntegracoes(this.appConfig.empresa)))
      .subscribe(list => {
        this.$showIntegracao.next(!!list?.length && this.paramsConfig.visualizacaoOcorrenciasSSW);
        this.updateMenuVisibility();
      });

    // Verificar configurações do sistema para Prestação de Contas (Despesas)
    this.ngrxStore.pipe(select(entityConfigPrestacaoContas(this.appConfig.empresa)))
      .subscribe(ativo => {
        this.$showPrestacaoContas.next(ativo && this.paramsConfig.visualizacaoTiposDespesas && (this.isAdmin || this.isCustomUser || this.isFinanceiro));
        this.updateMenuVisibility();
      });

    // Verificar configurações do sistema para Tratativas
    this.ngrxStore.pipe(select(entityConfigTrataviva(this.appConfig.empresa)))
      .subscribe(ativo => {
        this.$showTratativas.next(ativo && this.paramsConfig.visualizacaoTratativas);
        this.updateMenuVisibility();
      });
  }

  /**
   * Atualiza a visibilidade de cada item do menu baseado nas permissões
   */
  private updateMenuVisibility(): void {
    // DEBUG: Log detalhado das permissões
    console.log('=== DEBUG MENU VISIBILITY ===');
    console.log('paramsConfig completo:', JSON.stringify(this.paramsConfig, null, 2));
    console.log('visualizacaoLayoutsImportacao:', this.paramsConfig.visualizacaoLayoutsImportacao);
    console.log('visualizacaoParametrosSistema:', this.paramsConfig.visualizacaoParametrosSistema);
    console.log('visualizacaoIndicadores:', this.paramsConfig.visualizacaoIndicadores);
    console.log('visualizacaoDosIncidentes:', this.paramsConfig.visualizacaoDosIncidentes);
    console.log('visualizacaoLayoutsRelatorio:', this.paramsConfig.visualizacaoLayoutsRelatorio);
    console.log('visualizacaoTiposDespesas:', this.paramsConfig.visualizacaoTiposDespesas);
    console.log('visualizacaoOcorrenciasSSW:', this.paramsConfig.visualizacaoOcorrenciasSSW);
    console.log('visualizacaoTratativas:', this.paramsConfig.visualizacaoTratativas);
    console.log('isAdmin:', this.isAdmin);
    console.log('userRoles:', this.userRoles);
    console.log('=============================');

    // Painel (dashboard planejado x realizado) - somente Admin puro, como Grupo de Usuários
    this.showPainel$.next(this.isAdmin);

    // Sistema - visível se tem permissão de visualização
    this.showSistema$.next(this.paramsConfig.visualizacaoParametrosSistema);

    // Indicadores - visível se tem permissão de visualização
    this.showIndicadores$.next(this.paramsConfig.visualizacaoIndicadores);

    // Incidentes - visível se tem permissão de visualização
    this.showIncidentes$.next(this.paramsConfig.visualizacaoDosIncidentes);

    // Layouts de Importação - visível se tem permissão de visualização
    this.showImportacao$.next(this.paramsConfig.visualizacaoLayoutsImportacao);
    console.log('showImportacao$ setado para:', this.paramsConfig.visualizacaoLayoutsImportacao);

    // Relatório - visível se tem permissão de visualização
    this.showRelatorio$.next(this.paramsConfig.visualizacaoLayoutsRelatorio);

    // Despesas - visível se ativaPrestacaoContas no sistema E tem permissão E (isAdmin OU isFinanceiro)
    this.showDespesas$.next(this.$showPrestacaoContas.value);

    // Integração - visível se há integrações configuradas E tem permissão
    this.showIntegracaoMenu$.next(this.$showIntegracao.value);

    // Tratativa - visível se ativaTratativasIncidentes no sistema E tem permissão
    this.showTratativa$.next(this.$showTratativas.value);

    // Grupo de Usuários - visível se Admin E utilizaGrupoUsuario ativo no sistema
    this.showGrupoUsuario$.next(this.isAdmin && this.$utilizaGrupoUsuario);
  }

  /**
   * Verifica se o usuário é Admin puro (não Custom)
   * Custom são usuários com permissões personalizadas, não admins
   */
  get isAdmin(): boolean {
    return this.userRoles ? this.userRoles.includes('Admin') : false;
  }

  /**
   * Verifica se o usuário é Custom (operador com permissões customizadas)
   */
  get isCustomUser(): boolean {
    return this.userRoles ? this.userRoles.includes('Custom') : false;
  }

  /**
   * Verifica se o usuário é Financeiro
   */
  get isFinanceiro(): boolean {
    return this.userRoles ? this.userRoles.includes('Financeiro') : false;
  }

  /**
   * Verifica se deve mostrar o botão "Voltar ao Início"
   * Apenas Admins puros não devem ver este botão pois só tem acesso ao menu de configurações
   */
  get shouldShowBackButton(): boolean {
    return !this.isAdmin;
  }

  /**
   * Verifica se o usuário pode editar (Admin ou tem permissão específica)
   */
  get canEdit(): boolean {
    return this.isAdmin || this.paramsConfig.alteracaoParametrosAdministrativosSistema;
  }

  irParaUrl(url: string): void {
    this.router.navigate([url]);
  }

  logoutRedirect(): void {
    this.authService.desativar(true);
  }

  checkRoles(path: string, roles: { [k: string]: boolean } | null): boolean {
    return !!roles && roles[path];
  }

  voltarAoInicio(): void {
    this.voltarParaMenuPrincipal.emit();
  }
}
